import {
  LoadingSpinner,
  Select,
  type SelectOption,
  usePrevious,
} from '@solinkcorp/solink-components';
import { type HistogramRange } from '@solinkcorp/vd-common';
import { setRange } from 'actions/filterActions';
import { areCapsulesFilteredByDateTimeField } from 'components/EventFilter/utils';
import isEqual from 'lodash/isEqual';
import React, { useEffect } from 'react';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import EventFilterStore from 'stores/EventFilterStore';
import { useDispatch, useSelector } from 'stores/store';
import { useEventStandards } from 'utils/EventStandardCache/eventStandardCache';
import isShallowEqual from 'utils/isShallowEqual';
import { RANGE_DAY } from 'vd-constants';
import './RangeSelector.sass';

interface RangeSelectorProps {
  defaultRange?: HistogramRange;
  rangeOptions: SelectOption[];
}

const RangeSelector: React.FC<RangeSelectorProps> = (props) => {
  const { defaultRange, rangeOptions } = props;
  const dispatch = useDispatch();
  const locale = useLocale();
  const standards = useEventStandards();
  const fieldStats = useSelector((state) => state.fieldStats.fieldStats);
  const range = useSelector((state) => state.filter.range);

  const prevRangeOptions = usePrevious(rangeOptions);

  const { capsules } = useFluxStore(
    EventFilterStore,
    (store) => store.getAppState(),
    isShallowEqual,
  );

  // If range options change and currently selected range doesn't exist switch to default range
  useEffect(() => {
    if (
      !isEqual(prevRangeOptions, rangeOptions) &&
      !rangeOptions.some((option) => option.value === range)
    ) {
      dispatch(setRange(defaultRange || RANGE_DAY));
    }
  }, [defaultRange, dispatch, prevRangeOptions, range, rangeOptions]);

  const setFilterRange = ({ value }: { value: HistogramRange }) => {
    dispatch(setRange(value));
  };

  // Wait for fieldStats and featureStandard to load.
  if (!fieldStats || !standards) {
    return (
      <div id="range-selector-wrapper" className="loading">
        {locale.general.loading}
        <LoadingSpinner />
      </div>
    );
  }

  const isFilteredByDateTimeField = areCapsulesFilteredByDateTimeField(
    standards.eventStandard,
    fieldStats,
    capsules,
  );

  return (
    <div id="range-selector-wrapper">
      <Select
        className="filter-range"
        defaultValue={defaultRange}
        isSearchable={false}
        name="range"
        onChange={setFilterRange}
        options={rangeOptions}
        placeholder={locale.dashboard.rangePlaceholder}
        value={range}
        dataTestId="dashboard-filter-range"
        disabled={isFilteredByDateTimeField}
      />
    </div>
  );
};

export default RangeSelector;
